import { useState, useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  Mail,
  Upload,
  CheckCircle2,
  FileText,
  Clock,
} from 'lucide-react'
import {
  Button,
  Card,
  Checkbox,
  DataTable,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  currencyColumn,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@/components/ui'

interface LetterResult {
  letterId: string
  status: string
  trackingUrl?: string
  price?: number
}

interface LetterStatus {
  status: string
  trackingUrl?: string
  updatedAt?: string
}

export function BriefpostPage() {
  const [pdfBase64, setPdfBase64] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [sentLetters, setSentLetters] = useState<LetterResult[]>([])
  const [trackingLetterId, setTrackingLetterId] = useState('')
  const { toast } = useToast()

  const [recipient, setRecipient] = useState({
    firstName: '',
    lastName: '',
    company: '',
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    country: 'DE',
  })

  const [options, setOptions] = useState({
    isColor: false,
    isDuplex: false,
    isRegistered: false,
    registeredType: 'standard' as 'standard' | 'einwurf' | 'rueckschein',
  })

  const sendMutation = useMutation({
    mutationFn: (data: unknown) => api.post('/shipping/letter/send', data).then((r) => r.data),
    onSuccess: (data: LetterResult) => {
      setSentLetters((prev) => [data, ...prev])
      setPdfBase64(null)
      setFileName('')
      toast({ title: 'Brief versendet', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Fehler beim Versenden des Briefs', tone: 'danger' })
    },
  })

  const statusQuery = useQuery<LetterStatus>({
    queryKey: ['epost-status', trackingLetterId],
    queryFn: () => api.get(`/shipping/letter/${trackingLetterId}/status`).then((r) => r.data),
    enabled: !!trackingLetterId,
    retry: false,
  })

  const cancelMutation = useMutation({
    mutationFn: (letterId: string) => api.delete(`/shipping/letter/${letterId}`).then((r) => r.data),
    onSuccess: (_data, letterId) => {
      setSentLetters((prev) =>
        prev.map((l) => (l.letterId === letterId ? { ...l, status: 'cancelled' } : l)),
      )
      toast({ title: 'Brief storniert', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Fehler beim Stornieren', tone: 'danger' })
    },
  })

  const handleFileSelect = useCallback((file: File) => {
    if (file.type !== 'application/pdf') {
      toast({ title: 'Ungueltiger Dateityp', description: 'Bitte nur PDF-Dateien hochladen.', tone: 'warning' })
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      setPdfBase64(base64)
      setFileName(file.name)
    }
    reader.readAsDataURL(file)
  }, [toast])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!pdfBase64) return

    sendMutation.mutate({
      recipient,
      content: pdfBase64,
      contentType: 'pdf',
      isColor: options.isColor,
      isDuplex: options.isDuplex,
      isRegistered: options.isRegistered,
      registeredType: options.isRegistered ? options.registeredType : undefined,
    })
  }

  const letterColumns: ColumnDef<LetterResult>[] = [
    {
      accessorKey: 'letterId',
      header: 'Brief-ID',
      cell: ({ row }) => (
        <span className="font-mono text-caption text-gray-600">{row.original.letterId}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    currencyColumn<LetterResult>('price', 'Preis'),
    {
      id: '__actions',
      header: 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => {
        const letter = row.original
        const canCancel = letter.status !== 'cancelled' && letter.status !== 'dispatched'
        return (
          <div className="flex gap-2 justify-end">
            <Button
              variant="link"
              size="xs"
              onClick={() => setTrackingLetterId(letter.letterId)}
            >
              Status
            </Button>
            {canCancel && (
              <Button
                variant="linkDanger"
                size="xs"
                onClick={() => cancelMutation.mutate(letter.letterId)}
              >
                Stornieren
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="E-POSTBUSINESS Briefpost"
        description="Briefe digital versenden — gedruckt und zugestellt von der Post"
        breadcrumbs={[{ label: 'Versand', to: '/versand' }, { label: 'Briefpost' }]}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Empfaenger
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Vorname"
                value={recipient.firstName}
                onChange={(e) => setRecipient((r) => ({ ...r, firstName: e.target.value }))}
              />
              <Input
                label="Nachname"
                value={recipient.lastName}
                onChange={(e) => setRecipient((r) => ({ ...r, lastName: e.target.value }))}
              />
              <div className="md:col-span-2">
                <Input
                  label="Firma (optional)"
                  value={recipient.company}
                  onChange={(e) => setRecipient((r) => ({ ...r, company: e.target.value }))}
                />
              </div>
              <Input
                label="Strasse"
                required
                value={recipient.street}
                onChange={(e) => setRecipient((r) => ({ ...r, street: e.target.value }))}
              />
              <Input
                label="Hausnummer"
                required
                value={recipient.houseNumber}
                onChange={(e) => setRecipient((r) => ({ ...r, houseNumber: e.target.value }))}
              />
              <Input
                label="PLZ"
                required
                value={recipient.zipCode}
                onChange={(e) => setRecipient((r) => ({ ...r, zipCode: e.target.value }))}
              />
              <Input
                label="Stadt"
                required
                value={recipient.city}
                onChange={(e) => setRecipient((r) => ({ ...r, city: e.target.value }))}
              />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              PDF-Dokument
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? 'border-brand-400 bg-brand-50'
                  : pdfBase64
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {pdfBase64 ? (
                <div className="space-y-2">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto" />
                  <p className="text-small font-medium text-green-800">{fileName}</p>
                  <Button
                    type="button"
                    variant="linkDanger"
                    size="xs"
                    onClick={() => {
                      setPdfBase64(null)
                      setFileName('')
                    }}
                  >
                    Entfernen
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                  <p className="text-small text-gray-600">
                    PDF hierher ziehen oder{' '}
                    <label className="text-brand-700 hover:text-brand-800 cursor-pointer font-medium">
                      Datei auswaehlen
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileSelect(file)
                        }}
                      />
                    </label>
                  </p>
                  <p className="text-caption text-gray-400">Nur PDF-Dateien</p>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>Versandoptionen</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="space-y-3">
              <Checkbox
                label="Farbdruck"
                checked={options.isColor}
                onChange={(e) => setOptions((o) => ({ ...o, isColor: e.target.checked }))}
              />
              <Checkbox
                label="Duplexdruck (beidseitig)"
                checked={options.isDuplex}
                onChange={(e) => setOptions((o) => ({ ...o, isDuplex: e.target.checked }))}
              />
              <Checkbox
                label="Einschreiben"
                checked={options.isRegistered}
                onChange={(e) => setOptions((o) => ({ ...o, isRegistered: e.target.checked }))}
              />
              {options.isRegistered && (
                <div className="ml-6 max-w-xs">
                  <Select<'standard' | 'einwurf' | 'rueckschein'>
                    label="Einschreibenart"
                    value={options.registeredType}
                    onValueChange={(v) => setOptions((o) => ({ ...o, registeredType: v }))}
                    options={[
                      { value: 'standard', label: 'Standard' },
                      { value: 'einwurf', label: 'Einwurf-Einschreiben' },
                      { value: 'rueckschein', label: 'Einschreiben mit Rueckschein' },
                    ]}
                  />
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        <Button
          type="submit"
          variant="primary"
          loading={sendMutation.isPending}
          disabled={!pdfBase64 || sendMutation.isPending}
        >
          {sendMutation.isPending ? 'Wird versendet…' : 'Brief versenden'}
        </Button>
      </form>

      {sentLetters.length > 0 && (
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Versendete Briefe
            </Card.Title>
          </Card.Header>
          <DataTable<LetterResult>
            data={sentLetters}
            columns={letterColumns}
            getRowId={(row) => row.letterId}
            className="rounded-none border-0 shadow-none"
          />
        </Card>
      )}

      {trackingLetterId && statusQuery.data && (
        <Card className="border-blue-200 bg-blue-50">
          <Card.Body className="space-y-1">
            <p className="text-small font-medium text-blue-800">
              Status fuer Brief {trackingLetterId}
            </p>
            <StatusBadge status={statusQuery.data.status} />
            {statusQuery.data.trackingUrl && (
              <a
                href={statusQuery.data.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-caption text-blue-700 hover:underline mt-1 inline-block"
              >
                Sendungsverfolgung
              </a>
            )}
          </Card.Body>
        </Card>
      )}

    </div>
  )
}
